import { completeLegalRedirect, readLegalRedirectAttempt } from '../legal/legalOutbox';
import React, {
  createContext,
  Suspense,
  lazy,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';

import { BOOT_AUTH_STORAGE_KEYS } from '../../firebaseConfig';
import type { BoardSummary } from '../v4/accountState';
import { logEvent } from '../lib/eventSink';
import { gaSignInOpened } from '../lib/gaPixel';
import { consumeAuthRedirectResult, hasPendingAuthRedirect } from './accountAuth';
import {
  useAuthConfirmation,
  readAndClearRedirectConfirmation,
  mapRedirectAuthOutcome,
} from './authConfirmation';
import type { RedirectOutcome } from './redirectOutcome';
import { detectRedirectReturn, type RedirectReturnSignal } from './redirectReturn';
import { RedirectFailedToast } from './RedirectFailedToast';
import { SignInModal, type AuthMode } from './SignInModal';
import { hasPendingOtpInUrl } from './useOtpSignIn';
import { isInAppBrowser } from '../lib/inAppBrowser';
import { isV4RecoveryDoors } from '../lib/v4Preview';

export type { AuthMode } from './SignInModal';

// LAZY on purpose: V4SignInModal pulls captureWrite → callables → firebase
// functions at module load. Only `recovery` mode ever renders it, so a static
// import would drag firebase into AuthGateContext's module graph — crashing
// every test/surface that mounts AuthGate. React.lazy defers the import to the
// first render, which only happens under recovery mode.
const V4SignInModalLazy = lazy(() =>
  import('../v4/V4SignInModal').then((m) => ({ default: m.V4SignInModal })),
);

/** Visible, accessible loading state shown while the lazy v4 sign-in chunk loads
 *  after a sign-in click — so the recovery/incident path never shows a blank
 *  screen. Shape-matches the modal scrim so there's no layout jump. */
const V4SignInLoadingFallback: React.FC = () => (
  <div
    role="status"
    aria-live="polite"
    aria-label="Loading sign-in"
    data-testid="v4-signin-loading"
    style={{
      position: 'fixed',
      inset: 0,
      zIndex: 80,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'rgba(28, 22, 17, 0.44)',
    }}
  >
    <span
      style={{
        background: '#fff',
        padding: '14px 22px',
        borderRadius: 14,
        font: '600 14px/1.2 -apple-system, BlinkMacSystemFont, system-ui, sans-serif',
        color: '#3a2c20',
        boxShadow: '0 8px 28px rgba(80, 40, 20, 0.18)',
      }}
    >
      Loading sign-in…
    </span>
  </div>
);

/** Shown if the lazy sign-in chunk FAILS to load (stale deploy / removed chunk /
 *  transient network). Stays a modal, offers Reload + Close — so the recovery
 *  incident path degrades to a recoverable state, never a blank app tree. */
const V4SignInLoadFailed: React.FC<{ onClose: () => void }> = ({ onClose }) => (
  <div
    role="alertdialog"
    aria-label="Sign-in could not load"
    data-testid="v4-signin-load-failed"
    style={{
      position: 'fixed',
      inset: 0,
      zIndex: 80,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'rgba(28, 22, 17, 0.44)',
    }}
  >
    <div
      style={{
        background: '#fff',
        maxWidth: 360,
        padding: '22px 24px',
        borderRadius: 18,
        font: '400 14px/1.4 -apple-system, BlinkMacSystemFont, system-ui, sans-serif',
        color: '#3a2c20',
        boxShadow: '0 24px 48px rgba(80, 40, 20, 0.22)',
      }}
    >
      <div style={{ fontWeight: 600, marginBottom: 8 }}>Sign-in couldn’t load</div>
      <div style={{ marginBottom: 16 }}>Please reload and try again.</div>
      <button
        type="button"
        onClick={() => window.location.reload()}
        style={{ marginRight: 10, padding: '8px 16px', borderRadius: 10, border: 'none', background: '#3a2c20', color: '#fff', fontWeight: 600 }}
      >
        Reload
      </button>
      <button
        type="button"
        onClick={onClose}
        style={{ padding: '8px 16px', borderRadius: 10, border: '1px solid #d8ccbe', background: '#fff', color: '#3a2c20' }}
      >
        Close
      </button>
    </div>
  </div>
);

/** Narrow error boundary around the lazy v4 modal — a rejected dynamic import must
 *  not throw through AuthGateProvider and blank the app. Logs with the release mode
 *  for provenance. */
class V4SignInModalBoundary extends React.Component<
  { onClose: () => void; children: ReactNode },
  { failed: boolean }
> {
  state = { failed: false };
  static getDerivedStateFromError(): { failed: boolean } {
    return { failed: true };
  }
  componentDidCatch(error: unknown): void {
    try {
      const mode = (window as unknown as { __V4_MODE__?: string }).__V4_MODE__ ?? 'unknown';
      // eslint-disable-next-line no-console
      console.error('[v4 sign-in] modal chunk failed to load', { mode, error });
    } catch {
      /* logging must never throw */
    }
  }
  render(): ReactNode {
    if (this.state.failed) return <V4SignInLoadFailed onClose={this.props.onClose} />;
    return this.props.children;
  }
}

/**
 * TEMPORARY DIAGNOSTIC (mobile redirect sign-in loss, 2026-07-13) — remove
 * together with `BOOT_AUTH_STORAGE_KEYS` in firebaseConfig once closed.
 *
 * Captures the three facts that localize where a lost redirect credential
 * died, without needing Web Inspector on the device:
 *  - `boot_keys`: which SDK/app sessionStorage records the return load
 *    ARRIVED with (snapshotted before auth init consumes them). Missing
 *    `firebase:pendingRedirect`/`redirectUser` ⇒ consumed by an earlier load
 *    or never written; present ⇒ the SDK saw them and still resolved null.
 *  - `nav_type`: reload / back_forward vs navigate ⇒ hidden double-load.
 *  - `auth_resources`: which auth network legs actually ran this load
 *    (`/__/auth/iframe`, `signInWithIdp`, …) via the Performance API — the
 *    Network tab, programmatically.
 */
/**
 * TEMPORARY DIAGNOSTIC (with the above): one compact line for the toast —
 * the screen is the only transport available (sink rejects the event name,
 * no device inspector). pr/ru = SDK pendingRedirect/redirectUser records at
 * boot; mk = our marker; ifr/idp = whether the auth iframe / signInWithIdp
 * network legs ran this load.
 */
function summarizeRedirectDiagnostics(
  d: Record<string, unknown>,
  errorCode: string | null,
): string {
  const keys = String(d.boot_keys ?? '');
  const res = String(d.auth_resources ?? '');
  const has = (needle: string, hay: string) => (hay.includes(needle) ? 'Y' : 'N');
  return [
    `pr:${has('firebase:pendingRedirect', keys)}`,
    `ru:${has('firebase:redirectUser', keys)}`,
    `mk:${has('thea:authRedirect', keys)}`,
    `nav:${String(d.nav_type ?? '?')}`,
    `ifr:${has('/__/auth/iframe', res)}`,
    `idp:${has('signInWithIdp', res)}`,
    `ref:${String(d.referrer_host ?? '') || '-'}`,
    `err:${errorCode ?? '-'}`,
  ].join(' ');
}

function collectRedirectLostDiagnostics(): Record<string, unknown> {
  try {
    const nav = performance.getEntriesByType?.('navigation')?.[0] as
      | PerformanceNavigationTiming
      | undefined;
    const authResources = (performance.getEntriesByType?.('resource') ?? [])
      .map((e) => e.name)
      .filter((n) => /__\/auth\/|signInWithIdp|accounts\.google|appleid\.apple/.test(n))
      .map((n) => n.replace(/^https?:\/\//, '').replace(/\?.*$/, '').slice(0, 90))
      .slice(0, 12);
    let referrerHost = '';
    try {
      referrerHost = document.referrer ? new URL(document.referrer).host : '';
    } catch {
      referrerHost = '<unparseable>';
    }
    return {
      boot_keys: BOOT_AUTH_STORAGE_KEYS.join('|') || '<none>',
      nav_type: nav?.type ?? 'unknown',
      referrer_host: referrerHost,
      auth_resources: authResources.join('|') || '<none>',
    };
  } catch {
    return { boot_keys: '<diag-error>' };
  }
}

export interface SignInOptions {
  /** "signin" or "signup" — controls which view the modal opens to. */
  mode?: AuthMode;
  /** Fires once after a successful sign-in/sign-up, before the modal closes. */
  onAuthed?: () => void | Promise<void>;
  /**
   * Fires once if the modal closes WITHOUT anyone signing in — "Not now",
   * scrim, swipe, or Escape, which all funnel through the modal's `onClose`.
   * Mutually exclusive with `onAuthed` by construction (see `handleAuthed`),
   * so a caller can treat them as success vs. cancel.
   *
   * Exists because a gate can be the LANDING rather than an interruption:
   * `/board/:id` opens this sheet when a logged-out visitor lands on a board
   * they can't see (THE-797), and a visitor who declines has nowhere to be
   * returned to — the route owns where they go instead. The shared modal's own
   * dismiss behaviour is unchanged; it still just closes.
   */
  onDismissed?: () => void;
  /**
   * One extra line of context rendered under the modal's heading, for THIS
   * entry only. Omitted (the default at every other door) → the sheet is
   * exactly as it was. Keeps entry-specific copy with the entry that needs it
   * instead of growing an entry switch inside the shared modal.
   */
  contextLine?: string;
  /**
   * Which door the user came through — the CTA's `data-signin-entry` value
   * (homepage-header, header-pill, you-page, …) or a programmatic-gate name
   * (mark-purchased). Carried on the GA4 `sign_in_opened` event so the auth
   * funnel can be split by entry. Omitted → 'unknown'.
   */
  entry?: string;
  /**
   * Her boards, for the tap-opened sign-in pop-up's headline + pills
   * (THE-975 / THE-967 PRD "3. The sign-in pop-up"). Only Hub-gated callers
   * with boards to show pass this — every other caller omits it, and the
   * modal's existing no-boards fallback ('Log in'/'Create account') is
   * unchanged. Every entry through `requestSignIn` is a TAP by construction
   * (the timed prompt never goes through this chokepoint — see
   * `V4CaptureSheet.tsx`), so passing `boards` here always means the new
   * `variant: 'tap'` wording, never THE-962's.
   */
  boards?: BoardSummary[];
}

export interface AuthGateValue {
  /**
   * Open the auth modal. If the user successfully authenticates, `onAuthed`
   * fires once and is then cleared. Use this for save-intent preservation:
   * the heart button passes the "commit this heart to Firestore" closure so
   * it runs the moment the user is signed in.
   */
  requestSignIn: (options?: SignInOptions) => void;
  /**
   * True while the auth modal (opened by `requestSignIn`, any entry) is on
   * screen. A TAP entry (header, "Bought it", etc.) closes the surface that
   * opened it before this goes true, so a consumer coordinating a
   * one-popup-at-a-time rule (THE-962's capture sheet) cannot infer "auth is
   * open" from its own PDP/account-menu state — this is the only signal for
   * it. Mirrors the same `open` state that drives both the v4 and legacy
   * modal render below.
   */
  isOpen: boolean;
  /**
   * True when a mobile OAuth redirect was initiated (marker present in
   * sessionStorage) but the credential never came back on return. Surfaces
   * a "sign-in didn't complete" affordance so the user isn't silently left
   * anonymous. Bug 2 detection — only ever true on a post-redirect mount.
   */
  redirectFailed: boolean;
  /** Dismiss the redirect-failed affordance after the user acknowledges it. */
  dismissRedirectFailed: () => void;
  /**
   * True while a mobile OAuth redirect return is being finalized —
   * `getRedirectResult` (plus any credential-already-in-use recovery/merge)
   * hasn't settled yet. During this window the restored ANON session is on
   * screen but about to be replaced; pages can render a loading skeleton
   * instead of the soon-to-be-wrong layout (Kate 2026-07-06: shimmer, not
   * a stale marketing page). Bounded by a safety timeout so a hung
   * getRedirectResult can't skeleton the page forever.
   */
  redirectPending: boolean;
  /**
   * The resolved outcome of a mobile OAuth redirect return (see
   * `RedirectOutcome`). This provider is the single caller of
   * `getRedirectResult`; consumers read the result here rather than racing a
   * second `getRedirectResult`/`onAuthStateChanged`. The /claim route uses it
   * to finish (or recover from) a Google/Apple claim without an unbounded wait.
   */
  redirectOutcome: RedirectOutcome;
}

/**
 * Exported so consumers on shared UI surfaces can read this context safely
 * with `useContext(AuthGateContext)` and treat `null` as "no gate mounted on
 * this host" — without `useAuthGate` throwing.
 */
export const AuthGateContext = createContext<AuthGateValue | null>(null);

// Ceiling on how long `redirectPending` may hold loading UI. The finalize
// round-trip is ~1-2s; recovery + merge can stretch it. Past this we assume
// the redirect result is not coming (network hang) and release the page —
// same defense-in-depth stance as MergeStateProvider's safety timeout.
const REDIRECT_SETTLE_TIMEOUT_MS = 8_000;

export const AuthGateProvider: React.FC<{
  children: ReactNode;
  /** Override the redirectPending safety-timeout for tests. */
  redirectSettleTimeoutMs?: number;
}> = ({ children, redirectSettleTimeoutMs = REDIRECT_SETTLE_TIMEOUT_MS }) => {
  // Open straight away when the URL carries a pending OTP code step — an
  // in-app browser wiped in-memory state while the user was in their Mail app
  // fetching the code, so re-land them on the code screen instead of a blank
  // (webview app-switch survival; the pending email lives in the URL and
  // useOtpSignIn restores it into the modal). Read once at mount via lazy init.
  const [open, setOpen] = useState<boolean>(() => hasPendingOtpInUrl());
  // Default 'signin': an entry point that states no intent is far likelier to
  // be a generic sign-in door than a save-intent gate (the one save-intent
  // caller, mark-purchased, passes 'signup' explicitly). Since #291 removed the
  // provider buttons, this value is what the heading says.
  const [mode, setMode] = useState<AuthMode>('signin');
  const [redirectFailed, setRedirectFailed] = useState(false);
  // TEMPORARY DIAGNOSTIC — see summarizeRedirectDiagnostics above.
  const [redirectDiag, setRedirectDiag] = useState<string | undefined>(undefined);
  // Evidence that THIS load is the return leg of an OAuth redirect (THE-580).
  // Seeded at mount — BEFORE `consumeAuthRedirectResult` reads and clears the
  // marker — so the safety-timeout path (consume never settles) has evidence
  // too, and upgraded below with the marker consume actually read. See
  // `redirectReturn.ts` for why the marker alone is not enough.
  const [redirectReturn, setRedirectReturn] = useState(() =>
    detectRedirectReturn(hasPendingAuthRedirect()),
  );
  // Lazy-init from the redirect marker so the very first render of a
  // post-redirect load is already "pending" — no anon-layout flash frame.
  const [redirectPending, setRedirectPending] = useState<boolean>(() =>
    hasPendingAuthRedirect(),
  );
  // Published redirect outcome — the single-owner handoff (see RedirectOutcome).
  // Starts 'pending'; the consume effect resolves it to a terminal value, and
  // the safety timeout guarantees terminal even if consume never settles.
  const [redirectOutcome, setRedirectOutcome] = useState<RedirectOutcome>({
    status: 'pending',
  });
  const onAuthedRef = useRef<SignInOptions['onAuthed']>(undefined);
  const onDismissedRef = useRef<SignInOptions['onDismissed']>(undefined);
  // Set the moment a sign-in succeeds, so the `onClose()` that follows it is
  // not reported to the caller as a cancel. The modal calls `onSignedIn` then
  // `onClose` synchronously, which is what makes this flag reliable.
  const authedRef = useRef(false);
  // Entry-scoped context copy for the sheet (see `SignInOptions.contextLine`).
  const [contextLine, setContextLine] = useState<string | undefined>(undefined);
  // Entry-scoped boards for the tap pop-up's headline + pills (THE-975; see
  // `SignInOptions.boards`). Same lifecycle as `contextLine`: set on open,
  // cleared on close so a later boards-less entry can't inherit it.
  const [boards, setBoards] = useState<BoardSummary[] | undefined>(undefined);

  // The post-auth confirmation surface (spec §3.3). The synchronous popup / email
  // paths confirm inside V4SignInModal; a mobile OAuth REDIRECT left the page, so
  // its outcome is confirmed HERE on the return leg, from the stash the modal wrote
  // before redirecting. `confirm` is a stable no-op if no provider is mounted.
  const { confirm } = useAuthConfirmation();

  // Mobile Google/Apple sign-in returns via full-page redirect, landing on a
  // fresh app load with the modal closed. Firebase's OWN startup code
  // (`initializeCurrentUser`) completes a pending redirect and resolves the
  // permanent `currentUser` during Auth init — BEFORE any app code, including
  // this effect's `getRedirectResult` call, ever runs (this is the race
  // behind THE-1375: a listener mounted after startup never sees the guest
  // moment). We still call `getRedirectResult` here ourselves, at the root,
  // because it's the only way to read the credential payload (new-vs-existing,
  // recovered-collision) that Firebase's internal consume doesn't expose to
  // app code. Consuming here means the result lands regardless of which page
  // the user returns to. This works in tandem with same-origin `authDomain`
  // (configured in `.env`) so the credential the auth handler stored is in
  // the same origin's IndexedDB and isn't partitioned away by the browser.
  //
  // If a redirect-marker is present but no user came back, it's Bug 2 — fire
  // telemetry and surface the failure so the user isn't silently anon.
  useEffect(() => {
    const returningLegalAttempt = readLegalRedirectAttempt();
    consumeAuthRedirectResult()
      .then((result) => {
        completeLegalRedirect(result.user ?? null, returningLegalAttempt);
        // Read + CLEAR the redirect confirmation stash on EVERY return, so a lost
        // or bailed redirect can never leave a stale record that fires a
        // confirmation on a later load. It's used only on the success branch.
        const pending = readAndClearRedirectConfirmation();
        // Publish the explicit outcome for the single-owner handoff. A provider
        // user → success (isNewUser distinguishes fresh vs. existing); a
        // suppressed error code → error; neither → no-result (getRedirectResult
        // resolved null — lost, or never pending).
        if (result.user) {
          setRedirectOutcome({ status: 'success', isNewUser: result.isNewUser });
          // Derive the verified outcome from the operation we actually initiated
          // (link vs. signin) rather than trusting `isNewUser` alone — a
          // SUCCESSFUL `linkWithRedirect` reports `isNewUser: false` for a
          // brand-new account, because Identity Toolkit attaches the identity to
          // the uid that already existed (THE-712). See `mapRedirectAuthOutcome`'s
          // docblock for the full table. Computed unconditionally (not only when a
          // confirmation toast is pending) because THE-1375's signup report below
          // needs it on every redirect return, toast or not.
          const mapped = mapRedirectAuthOutcome(result);
          // THE-1375: report the verified "account created" outcome directly,
          // instead of a shared listener inferring it later from auth-state
          // timing. This is the only place a mobile OAuth redirect return can be
          // reported from — the modal that initiated it unmounted when the page
          // navigated away for the provider round trip.
          //
          // DYNAMIC import, same reason `V4SignInModalLazy` above is lazy:
          // `reportAccountCreated` pulls `trackSignup` → `../callables` →
          // firebase/functions at module load. A static import here would drag
          // that into AuthGateContext's module graph — crashing every test/
          // surface that mounts AuthGate (most of the app, via HeaderAccountMenu)
          // without itself mocking firebase.
          if (mapped.account === 'created') {
            const user = result.user;
            void import('../hooks/useGaUserIdentity')
              .then((m) => m.reportAccountCreated(user))
              // A stale deploy / removed chunk / network blip on this import
              // must not silently lose the welcome report with no trace —
              // log it so it's at least visible, same as the redirect-result
              // failure above.
              .catch((err) => {
                console.error('[AuthGate] reportAccountCreated chunk load failed:', err);
              });
          }
          // Confirm a mobile OAuth redirect return (spec §3.3). Fire ONLY when a
          // redirect we initiated actually came back: `markerPresent` (v4Auth wrote
          // the redirect marker before navigating) correlates this result with our
          // stash, so a stale stash can't pair with an unrelated same-tab redirect
          // (Codex finding 4). Absent either, say nothing.
          //
          // A NEW account = the anon identity was linked in place → guestData
          // 'linked'; an EXISTING account = the guest board is LEFT BEHIND
          // (Decision 7 / feedback_no_merge_into_existing_account) → 'none', so no
          // toast. Whether "board saved" is SHOWN is the mapper's call (gated on
          // boardCount>0), so a no-board new account falls through to
          // "Your account is ready."
          //
          // The existing-account recovery leg (accountAuth's
          // `credential-already-in-use` catch) signs into the existing account
          // only — it does not merge the guest board (#414) — so 'none' here
          // matches what the code actually does, not just the intent.
          if (pending && result.markerPresent) {
            confirm({
              ...mapped,
              entryContext: pending.entryContext,
              boardCount: pending.boardCount,
            });
            // THE-966: the capture sheet's own React tree (and `useV4CaptureTrigger`'s
            // `onCaptured`) was destroyed by the full-page redirect, so this is the
            // only place that can still fire `capture_sheet_signed_up` for a mobile
            // Google/Apple signup started from that sheet. Gated on `fromCaptureSheet`
            // (stashed by `V4CaptureSheet`'s mount of this modal) so a redirect from
            // any OTHER door carrying boards (`BoardSignInBackdrop`, …) stays silent —
            // same isolation the synchronous path's `onCaptured` already has.
            if (pending.fromCaptureSheet) {
              logEvent('capture_sheet_signed_up', { account: mapped.account });
            }
          }
        } else if (result.errorCode) {
          setRedirectOutcome({ status: 'error', code: result.errorCode });
        } else {
          setRedirectOutcome({ status: 'no-result' });
        }
        // Fold in the marker consume actually read. The mount-time seed used
        // `hasPendingAuthRedirect()`, which additionally ages the marker out
        // after 10 minutes; consume does not. A user who took longer than that
        // at the provider still came back from a redirect.
        if (result.markerPresent) {
          setRedirectReturn((prev) =>
            prev.signals.includes('marker')
              ? prev
              : { isReturn: true, signals: [...prev.signals, 'marker' as RedirectReturnSignal] },
          );
        }
      })
      .catch((err) => {
        console.error('[AuthGate] consumeAuthRedirectResult failed:', err);
        setRedirectOutcome({
          status: 'error',
          code: (err as { code?: string })?.code,
        });
      })
      .finally(() => {
        // Settled on every path — success, recovery, or failure. Pages
        // holding a skeleton on redirectPending must always release.
        setRedirectPending(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // THE LOST-REDIRECT CHOKEPOINT (THE-580). One place decides that a mobile
  // OAuth redirect came back without a credential, and it both FIRES THE EVENT
  // and raises the on-screen failure — so the two can never disagree again.
  //
  // Keyed on the published `redirectOutcome` rather than on the consume
  // callback, which buys two things the old in-`.then` version did not have:
  //   - `timeout` is covered. A consume that never settles is a lost redirect
  //     too, and it used to produce silence on BOTH channels.
  //   - `error` is covered with its code.
  //
  // Both conjuncts are load-bearing. `redirectOutcome` alone is not a signal:
  // it resolves `no-result` on every ordinary cold load, so firing on it would
  // report a failed sign-in to every first-time visitor. `redirectReturn` is
  // the positive evidence that a provider round-trip actually happened.
  //
  // The ref guard makes it EXACTLY ONE event per load: React 18 StrictMode
  // double-invokes effects on the same instance in dev, and the outcome can
  // also settle twice (consume resolving after the safety timeout already
  // published `timeout`).
  const lostRedirectReportedRef = useRef(false);
  useEffect(() => {
    if (lostRedirectReportedRef.current) return;
    if (redirectOutcome.status === 'pending' || redirectOutcome.status === 'success') return;
    if (!redirectReturn.isReturn) return;
    lostRedirectReportedRef.current = true;

    const errorCode =
      redirectOutcome.status === 'error' ? redirectOutcome.code ?? null : null;
    const diag = collectRedirectLostDiagnostics();
    logEvent('auth_redirect_lost', {
      // Which terminal branch we landed on: 'no-result' | 'error' | 'timeout'.
      outcome: redirectOutcome.status,
      error_code: errorCode,
      // Which evidence caught it. The interesting query is how often this is
      // NOT 'marker' — that is the population the old marker-gated fire was
      // blind to, and the reason this event went quiet instead of going to
      // zero.
      detection: redirectReturn.signals.join('|'),
      surface: 'auth_gate',
      ...diag,
    });
    setRedirectDiag(summarizeRedirectDiagnostics(diag, errorCode));
    setRedirectFailed(true);
  }, [redirectOutcome, redirectReturn]);

  // Safety net: if the finalize round-trip hangs, release the pending UI
  // after the ceiling rather than skeleton-ing the page indefinitely.
  useEffect(() => {
    if (!redirectPending) return;
    const timer = setTimeout(() => {
      setRedirectPending(false);
      // Guarantee a terminal outcome even if consume never resolves.
      setRedirectOutcome((prev) =>
        prev.status === 'pending' ? { status: 'timeout' } : prev,
      );
    }, redirectSettleTimeoutMs);
    return () => clearTimeout(timer);
  }, [redirectPending, redirectSettleTimeoutMs]);

  const requestSignIn = useCallback((options?: SignInOptions) => {
    const mode = options?.mode ?? 'signin';
    // Single chokepoint for "the auth door opened" — every CTA and
    // programmatic gate funnels through here, so one fire covers them all
    // (same rationale as gaSignUp watching the auth-state transition).
    gaSignInOpened({ entry: options?.entry ?? 'unknown', mode });
    setMode(mode);
    onAuthedRef.current = options?.onAuthed;
    onDismissedRef.current = options?.onDismissed;
    authedRef.current = false;
    setContextLine(options?.contextLine);
    setBoards(options?.boards);
    setOpen(true);
  }, []);

  const handleAuthed = useCallback(async () => {
    // FIRST, and synchronously: the modal closes right after this resolves, and
    // that close must not fire the caller's cancel handler.
    authedRef.current = true;
    const cb = onAuthedRef.current;
    onAuthedRef.current = undefined;
    if (cb) {
      try {
        await cb();
      } catch (err) {
        console.error('[AuthGate] onAuthed callback failed:', err);
      }
    }
  }, []);

  const handleOpenChange = useCallback((next: boolean) => {
    setOpen(next);
    if (next) return;
    // Every dismissal affordance the modal has — "Not now", scrim, swipe,
    // Escape — lands here, and so does the close that follows a SUCCESSFUL
    // sign-in. `authedRef` is what tells them apart.
    const dismissed = onDismissedRef.current;
    onAuthedRef.current = undefined;
    onDismissedRef.current = undefined;
    setContextLine(undefined);
    setBoards(undefined);
    if (!authedRef.current && dismissed) {
      try {
        dismissed();
      } catch (err) {
        console.error('[AuthGate] onDismissed callback failed:', err);
      }
    }
  }, []);

  const dismissRedirectFailed = useCallback(() => {
    setRedirectFailed(false);
  }, []);

  // Lost-redirect retry: acknowledge the failure and reopen the sign-in
  // flow. Without this the redirectFailed state was telemetry-only and a
  // lost mobile sign-in dead-ended silently as an anonymous session.
  const retryAfterRedirectFailed = useCallback(() => {
    setRedirectFailed(false);
    requestSignIn({ mode: 'signin' });
  }, [requestSignIn]);

  return (
    <AuthGateContext.Provider
      value={{
        requestSignIn,
        isOpen: open,
        redirectFailed,
        dismissRedirectFailed,
        redirectPending,
        redirectOutcome,
      }}
    >
      {children}
      {redirectFailed && (
        <RedirectFailedToast
          onRetry={retryAfterRedirectFailed}
          onDismiss={dismissRedirectFailed}
          diagnostics={redirectDiag}
        />
      )}
      {/* Whenever v4 doors should be reachable — `on`, `recovery`, and the `qa`
          rehearsal route (i.e. isV4RecoveryDoors()) — this single shared modal
          mount renders the v4 Google/Apple/email doors instead of the legacy OTP
          modal. Every sign-in entry funnels through requestSignIn → here, so this
          one predicate guarantees NO entry can fall back to legacy OTP while v4 is
          active (the on-mode HeaderAccountMenu divergence Codex caught). Off the
          QA route in qa mode, and in off mode, isV4RecoveryDoors() is false → the
          legacy modal, unchanged. */}
      {isV4RecoveryDoors() ? (
        // Mount the lazy modal ONLY while open, so the chunk import fires on the
        // sign-in CLICK (not on page load — which would flash the fallback), and
        // a VISIBLE, accessible loading state covers the brief download instead of
        // a blank screen (Codex finding: blank-lazy-modal). Preload-on-idle + a
        // chunk-load error boundary are tracked as follow-up reliability hardening.
        open ? (
          <V4SignInModalBoundary onClose={() => handleOpenChange(false)}>
            <Suspense fallback={<V4SignInLoadingFallback />}>
              <V4SignInModalLazy
                open
                onClose={() => handleOpenChange(false)}
                browser={isInAppBrowser() ? 'webview' : 'normal'}
                intent="signin"
                onSignedIn={handleAuthed}
                // Entry-scoped copy, undefined at every other door — see
                // `SignInOptions.contextLine`.
                contextLine={contextLine}
                // THE-975: entry-scoped boards for the tap headline + pills —
                // see `SignInOptions.boards`. Every `requestSignIn` caller is a
                // tap by construction (the timed prompt bypasses this
                // chokepoint entirely), so `variant` is unconditionally 'tap';
                // it only takes effect when `boards` is actually present.
                boards={boards}
                variant="tap"
                initialMode={boards && boards.length > 0 ? 'create' : undefined}
                // THE-580: the modal's own terminal state for a lost redirect.
                // Injected as PROPS, not read from this context — `v4/` must
                // stay free of AuthGate (the account-door fitness invariant),
                // which is the same reason V4ClaimRoute takes the outcome by
                // prop from a bridge in App.js.
                //
                // `oauthReturn` is what stops `no-result` from being read as a
                // failure on an ordinary cold load; the modal additionally
                // tracks its OWN in-session attempt, which is the layer that
                // still works when every cross-load signal is gone.
                redirectOutcome={redirectOutcome}
                oauthReturn={redirectReturn.isReturn}
              />
            </Suspense>
          </V4SignInModalBoundary>
        ) : null
      ) : (
        <SignInModal
          open={open}
          onOpenChange={handleOpenChange}
          defaultMode={mode}
          onAuthed={handleAuthed}
        />
      )}
    </AuthGateContext.Provider>
  );
};

export function useAuthGate(): AuthGateValue {
  const ctx = useContext(AuthGateContext);
  if (!ctx) throw new Error('useAuthGate must be used within AuthGateProvider');
  return ctx;
}
